import React from "react";
import { HelpCircle, Users, Target, TrendingUp } from "lucide-react";
import type { FinancialHeaderProps } from "./FinancialHeader.types";
import { calculateMonthlyCustomerMetrics } from "../../../utils/chart/customer-projection.utils";
import { calculateBreakEvenPlan } from "../../../utils/chart/break-even.utils";

export const FinancialHeader: React.FC<FinancialHeaderProps> = ({
  currentDate,
  isPreStartup,
  isCurrentTimePast,
  isCurrentTimePresent,
  isCurrentTimeFuture,
  data,
  currentSnapshot,
  customerProjectionData,
  cashZeroEvent,
  onHelpModalOpen,
}) => {
  const customerMetrics = customerProjectionData
    ? calculateMonthlyCustomerMetrics(currentDate, customerProjectionData)
    : null;

  const cash = Math.round((currentSnapshot?.cashBalance || 0) * 100) / 100;
  const runway =
    currentSnapshot?.runway !== Infinity
      ? `${Math.floor(currentSnapshot?.runway || 0)} mo runway`
      : "∞ runway";

  const breakEvenPlan = calculateBreakEvenPlan(currentSnapshot);

  return (
    <div className="overflow-hidden rounded-t-[20px] border-b border-white/10 bg-gradient-to-r from-brand/10 via-surface-elevated to-focus/10 px-4 py-4 sm:px-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <p className="sh-kicker">Snapshot</p>
            <h3 className="text-lg font-bold text-white">
              {isPreStartup ? "Pre-startup period" : "Financial snapshot"}
            </h3>
            <p className="mt-1 text-sm text-white/65">
              {currentDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              {isPreStartup ? (
                <span className="ml-2 text-white/45">(before launch)</span>
              ) : null}
              {!isPreStartup && isCurrentTimePast ? (
                <span className="ml-2 text-white/45">· past</span>
              ) : null}
              {!isPreStartup && isCurrentTimePresent ? (
                <span className="ml-2 text-brand">· today</span>
              ) : null}
              {!isPreStartup && isCurrentTimeFuture ? (
                <span className="ml-2 text-violet-300">· projected</span>
              ) : null}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onHelpModalOpen}
              className="sh-icon-btn"
              title="How does the funding calculator work?"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="text-left lg:text-right">
          {isPreStartup ? (
            <>
              <div className="text-3xl font-black text-white/40">
                ${data.initialBalance.toLocaleString()}
              </div>
              <p className="sh-subtle mt-1">Initial balance · startup not active</p>
              <p className="sh-subtle">
                Starts {data.startDate.toLocaleDateString()} ·{" "}
                {Math.ceil(
                  (data.startDate.getTime() - currentDate.getTime()) /
                    (24 * 60 * 60 * 1000),
                )}{" "}
                days away
              </p>
            </>
          ) : (
            <>
              <div
                className={`text-3xl font-black tabular-nums ${
                  cash >= 0 ? "text-brand" : "text-expense"
                }`}
              >
                $
                {cash.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <p className="mt-1 text-sm font-semibold text-white/75">{runway}</p>
              {cashZeroEvent ? (
                <p
                  className={`mt-1 text-sm font-semibold ${
                    cashZeroEvent.isUpcoming ? "text-warn" : "text-expense"
                  }`}
                >
                  {cashZeroEvent.isUpcoming ? "Cash runs out" : "Cash negative since"}{" "}
                  {cashZeroEvent.date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/45 lg:justify-end">
                <span>
                  Revenue ${Math.round(currentSnapshot?.monthlyRevenue || 0).toLocaleString()}
                  /mo
                </span>
                <span
                  className={
                    (currentSnapshot?.netMonthlyChange || 0) >= 0
                      ? "text-revenue"
                      : "text-expense"
                  }
                >
                  Net $
                  {Math.round(
                    Math.abs(currentSnapshot?.netMonthlyChange || 0),
                  ).toLocaleString()}
                  /mo
                </span>
                {customerMetrics && customerMetrics.totalCustomers > 0 ? (
                  <span className="inline-flex items-center gap-1 text-brand">
                    <Users className="h-3 w-3" />
                    {customerMetrics.totalCustomers.toLocaleString()} customers
                  </span>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {!isPreStartup && breakEvenPlan ? (
        <div className="mt-4 grid gap-3 border-t border-white/10 pt-4 md:grid-cols-3">
          <div className="rounded-[14px] border border-white/10 bg-surface/30 px-3 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/45">
              <Target className="h-3.5 w-3.5" />
              Operating break-even
            </div>
            <p className="mt-1 text-lg font-bold text-white">
              ${Math.round(breakEvenPlan.operatingBreakEvenRevenue).toLocaleString()}
              <span className="text-sm font-medium text-white/45"> /mo revenue</span>
            </p>
            <p className="mt-1 text-xs text-white/45">
              {breakEvenPlan.isOperatingBreakEven
                ? "Revenue covers monthly burn."
                : `Need +$${Math.round(breakEvenPlan.monthlyRevenueGap).toLocaleString()}/mo to break even.`}
            </p>
          </div>

          <div className="rounded-[14px] border border-white/10 bg-surface/30 px-3 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/45">
              <TrendingUp className="h-3.5 w-3.5" />
              Growth plan
            </div>
            {breakEvenPlan.requiredMonthlyRevenueRamp ? (
              <>
                <p className="mt-1 text-lg font-bold text-warn">
                  +$
                  {Math.round(breakEvenPlan.requiredMonthlyRevenueRamp).toLocaleString()}
                  <span className="text-sm font-medium text-white/45"> /mo ramp</span>
                </p>
                <p className="mt-1 text-xs text-white/45">
                  Reach break-even within {breakEvenPlan.monthsBeforeCashZero} months before cash
                  runs out.
                </p>
              </>
            ) : (
              <p className="mt-1 text-sm text-brand">
                {breakEvenPlan.isOperatingBreakEven
                  ? "No ramp needed — you are at or above break-even."
                  : "Increase revenue to operating break-even to extend runway."}
              </p>
            )}
          </div>

          <div className="rounded-[14px] border border-white/10 bg-surface/30 px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/45">
              Current vs break-even
            </p>
            <p className="mt-1 text-lg font-bold text-white">
              ${Math.round(breakEvenPlan.currentMonthlyRevenue).toLocaleString()}
              <span className="text-sm font-medium text-white/45"> /mo now</span>
            </p>
            <p className="mt-1 text-xs text-white/45">
              Burn ${Math.round(breakEvenPlan.monthlyBurn).toLocaleString()}/mo · Cash $
              {Math.round(breakEvenPlan.cashBalance).toLocaleString()}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};
